import { Injectable, inject } from '@angular/core';
import {
  BlightData,
  Day,
  PotatoAgrifirmCropIds,
  selectBlightData,
  selectCustomerBlightRights,
} from '@next/arable-lib/blight';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { isDateToday } from '@next/arable-lib/shared';
import { select, Store } from '@ngrx/store';
import { GeoJSON } from 'ol/format';
import VectorLayer from 'ol/layer/Vector';
import { Vector } from 'ol/source';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { ReplaySubject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BlightSyncStatus, SiteState } from 'src/app/shared/models';

import { InteractionType } from '../../models';
import { SetBlightLayerEnabled } from '../../store/actions/map-context.actions';
import { getFeatures } from '../../store/selectors/feature.selectors';
import { getBlightLayerEnabled } from '../../store/selectors/map-context.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectBlightService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.selectBlightWarningsLayer];
  crops: Crop[] = [];

  readonly source = new Vector({
    wrapX: false,
  });

  readonly layer = new VectorLayer({
    source: this.source,
    style: this.getStyle,
    zIndex: 2,
  });

  private reader = new GeoJSON({});
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  init(geoService: GeoBrowserService): void {
    geoService.map.addLayer(this.layer);

    this.getCrops();

    combineLatest([
      this.store.select(selectBlightData),
      this.store.select(selectCustomerBlightRights),
      this.store.select(getFeatures),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([blightData, customerBlightRights, features]) => {
        if (
          customerBlightRights?.isPremium &&
          blightData &&
          blightData[0]?.syncStatus !== BlightSyncStatus.mock
        ) {
          this.source.clear();
          this.enableBlightWarningsLayer();

          features.forEach((f) => {
            const agrifirmCropId = this.crops.find((crop) => crop.id === f.cropId)?.agrifirmCrop
              ?.id;
            if (PotatoAgrifirmCropIds.includes(agrifirmCropId)) {
              const blightDataForToday = this.getBlightDataForToday(blightData, +f.id);
              if (blightDataForToday && blightDataForToday.sprayType > 0) {
                const feature = this.reader.readFeature(f);
                feature.setId(f.id);
                this.source.addFeature(feature);
              }
            }
          });
        }
      });

    this.store
      .pipe(select(getBlightLayerEnabled), takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.layer.setVisible(enabled);
      });
  }

  start(_: GeoBrowserService): void {}

  stop(_: GeoBrowserService): void {}

  getStyle() {
    return new Style({
      stroke: new Stroke({
        color: MapColors.blightPreventiveStroke,
        width: 2,
        lineDash: [1, 4],
        lineCap: 'round',
      }),
      fill: new Fill({
        color: MapColors.blightPreventiveFill,
      }),
    });
  }

  destroy(geoService: GeoBrowserService) {
    this.layer.dispose();
    geoService.map.removeLayer(this.layer);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private getCrops() {
    this.store.pipe(select(getCrops), takeUntil(this.destroyed$)).subscribe((crops) => {
      this.crops = crops;
    });
  }

  private enableBlightWarningsLayer() {
    this.store.dispatch(new SetBlightLayerEnabled(true));
  }

  private getBlightDataForToday(blightData: BlightData[], cropFieldId: number): Day {
    const blightDaysResult = blightData?.find((x) => x.cropFieldId === cropFieldId)?.blightResult
      ?.days;
    const blightWithSprayAdvice = blightDaysResult?.filter((x) => x.sprayType > 0);
    return blightWithSprayAdvice?.find((x) => isDateToday(x.date));
  }
}
